import {
  getAgentIntegrations,
  updateAgentIntegrations,
  deleteAgentIntegration,
  AgentIntegration,
} from "views/agent/api";
import { Integration } from "views/integrations/types";
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Card from "components/card";
import GenericTable from "components/table";
import { createColumnHelper } from "@tanstack/react-table";
import { getIntegrations } from "views/integrations/api";
import { Trash } from "lucide-react";
import { ConfirmationDialog } from "components/dialog/confirmation";
import { useTranslation } from "react-i18next";

interface DisplayIntegration extends Integration {
  agent_integration_id?: string;
  isInitial: boolean;
}

const columnHelper = createColumnHelper<DisplayIntegration>();

const AgentIntegrations = () => {
  const { t } = useTranslation();
  const { id: agentId } = useParams<{ id: string }>();

  const [allIntegrations, setAllIntegrations] = useState<Integration[]>([]);
  const [agentIntegrations, setAgentIntegrations] = useState<
    AgentIntegration[]
  >([]);
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<
    string[]
  >([]);
  const [integrationToDelete, setIntegrationToDelete] =
    useState<DisplayIntegration | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const [agentData, availableData] = await Promise.all([
        getAgentIntegrations(agentId),
        getIntegrations({ take: 1000, skip: 0, agent_id: agentId }),
      ]);

      const currentAgentIntegrations = Array.isArray(agentData)
        ? agentData
        : [];
      setAgentIntegrations(currentAgentIntegrations);
      setSelectedIntegrationIds(currentAgentIntegrations.map((i) => i.id));
      setAllIntegrations(availableData.items);
    } catch (err) {
      setError("Failed to fetch integrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [agentId]);

  const handleSelectionChange = (integrationId: string) => {
    setSelectedIntegrationIds((prev) =>
      prev.includes(integrationId)
        ? prev.filter((id) => id !== integrationId)
        : [...prev, integrationId]
    );
  };

  const handleRemoveClick = (integration: DisplayIntegration) => {
    setIntegrationToDelete(integration);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (
      !agentId ||
      !integrationToDelete ||
      !integrationToDelete.agent_integration_id
    )
      return;

    setIsDeleting(true);
    try {
      await deleteAgentIntegration(
        agentId,
        integrationToDelete.agent_integration_id
      );
      await fetchData();
    } catch (err) {
      setError("Failed to delete integration");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
      setIntegrationToDelete(null);
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    const initialIds = agentIntegrations.map((i) => i.id);
    const availableIds = allIntegrations
      .filter((i) => !initialIds.includes(i.id))
      .map((i) => i.id);

    if (isSelected) {
      setSelectedIntegrationIds([...initialIds, ...availableIds]);
    } else {
      setSelectedIntegrationIds(initialIds);
    }
  };

  const handleSaveChanges = async () => {
    if (!agentId) return;

    setIsSaving(true);
    try {
      await updateAgentIntegrations(agentId, selectedIntegrationIds);
      await fetchData();
    } catch (err) {
      setError("Failed to save integrations");
    } finally {
      setIsSaving(false);
    }
  };

  const tableData = useMemo(() => {
    const agentIntegrationsMap = new Map(
      agentIntegrations.map((i) => [i.id, i.agent_integration_id])
    );

    const merged = allIntegrations.map((integration) => ({
      ...integration,
      agent_integration_id: agentIntegrationsMap.get(integration.id),
      isInitial: agentIntegrationsMap.has(integration.id),
    }));

    return merged.sort((a, b) => {
      if (a.isInitial && !b.isInitial) return -1;
      if (!a.isInitial && b.isInitial) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [allIntegrations, agentIntegrations]);

  const hasChanges = useMemo(() => {
    const initialIds = agentIntegrations.map((i) => i.id).sort();
    const currentIds = [...selectedIntegrationIds].sort();
    return JSON.stringify(initialIds) !== JSON.stringify(currentIds);
  }, [selectedIntegrationIds, agentIntegrations]);

  const isAllAvailableSelected = useMemo(() => {
    const initialIds = new Set(agentIntegrations.map((i) => i.id));
    const availableIntegrations = allIntegrations.filter(
      (i) => !initialIds.has(i.id)
    );
    const selectedAvailableCount = selectedIntegrationIds.filter(
      (id) => !initialIds.has(id)
    ).length;

    return (
      availableIntegrations.length > 0 &&
      selectedAvailableCount === availableIntegrations.length
    );
  }, [selectedIntegrationIds, allIntegrations, agentIntegrations]);

  const areSomeAvailableSelected = useMemo(() => {
    const initialIds = new Set(agentIntegrations.map((i) => i.id));
    const availableIntegrations = allIntegrations.filter(
      (i) => !initialIds.has(i.id)
    );
    const selectedAvailableCount = selectedIntegrationIds.filter(
      (id) => !initialIds.has(id)
    ).length;

    return (
      selectedAvailableCount > 0 &&
      selectedAvailableCount < availableIntegrations.length
    );
  }, [selectedIntegrationIds, allIntegrations, agentIntegrations]);

  const columns = [
    columnHelper.accessor("id", {
      id: "select",
      header: () => (
        <input
          type="checkbox"
          checked={isAllAvailableSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-blue-600 dark:border-gray-600 dark:bg-gray-900"
        />
      ),
      cell: ({ row }) => {
        const isSelected = selectedIntegrationIds.includes(row.original.id);
        return (
          <input
            type="checkbox"
            checked={isSelected}
            disabled={row.original.isInitial}
            onChange={() => handleSelectionChange(row.original.id)}
            className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-blue-600 dark:border-gray-600 dark:bg-gray-900"
          />
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor("name", {
      header: t("common.name"),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("integration_type", {
      header: t("library.type"),
      cell: (info) => info.getValue(),
    }),
    columnHelper.display({
      id: "actions",
      header: t("library.actions"),
      cell: ({ row }) => {
        if (row.original.isInitial) {
          return (
            <button
              onClick={() => handleRemoveClick(row.original)}
              disabled={
                isDeleting && integrationToDelete?.id === row.original.id
              }
            >
              <Trash size={16} className="text-red-500" />
            </button>
          );
        }
        return null;
      },
    }),
  ];

  if (loading) return <div>{t("common.loading")}</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="p-8">
      <Card extra="w-full h-full p-4">
        <div className="mb-6">
          <h3 className="mb-2 text-xl font-bold">{t("integrations.manageIntegrations")}</h3>
          <GenericTable
            columns={columns}
            data={tableData}
            searchPlaceholder={t("integrations.searchForIntegration")}
          />
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSaveChanges}
              className="rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:opacity-50"
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? t("common.saving") : t("common.saveChanges")}
            </button>
          </div>
        </div>
      </Card>
      {integrationToDelete && (
        <ConfirmationDialog
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={confirmDelete}
          title={t("integrations.confirmRemoval")}
          message={t("integrations.confirmRemovalMessage", { name: integrationToDelete.name })}
          confirmText={isDeleting ? t("integrations.removing") : t("common.confirm")}
          cancelText={t("common.cancel")}
          variant="danger"
        />
      )}
    </div>
  );
};

export default AgentIntegrations;
